import { useEffect, useState } from 'react';

/**
 * Device-level network connectivity — `navigator.onLine` plus the standard
 * `online`/`offline` window events. Works identically on web and inside the
 * Capacitor WebView on native (Chromium on Android tracks real connectivity
 * changes through these same events; no native plugin needed for this).
 *
 * This is deliberately generic/global connectivity, not the game's own
 * ActionCable reconnect state — GameScene/GameScreen already have their own
 * resilience UX for a dropped WS connection during active play (see
 * GameScreen.tsx's spectator/participant reconnect handling), so a consumer
 * showing a banner from this hook should hide it on /game and /spectate to
 * avoid a second, redundant "you're offline" surface stacking on top of that
 * existing one (see OfflineBanner.tsx / AppShell.tsx).
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return isOnline;
}
